import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import PageHeader from '../shared/PageHeader';
import './SolicitationDetail.css';

function fmtDateTime(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-US', { dateStyle:'medium', timeStyle:'short' }); }
  catch (_) { return s; }
}

/*
 * Column key → header label. The key is the backend's sort vocabulary
 * (routes/solicitations.js SORTS), so a header and a sort can never drift into
 * naming the same column two ways.
 */
const COLUMNS = [
  { key: 'id',          label: 'Event ID' },
  { key: 'title',       label: 'Title' },
  { key: 'department',  label: 'Department' },
  { key: 'close_date',  label: 'Closes' },
  { key: 'attachments', label: 'Atts',     num: true },
  { key: 'sections',    label: 'Sections', num: true },
  { key: 'skus',        label: 'SKUs',     num: true },
];

/* '-close_date' → { key:'close_date', desc:true } */
const parseSort = (s) => ({ key: s.replace(/^-/, ''), desc: s.startsWith('-') });

export default function SolicitationList() {
  useDocumentTitle('Solicitations');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [hasSkus, setHasSkus] = useState(false);
  const [itOnly, setItOnly] = useState(false);
  const [sort, setSort] = useState('-close_date');

  const { data, isLoading, error } = useQuery({
    queryKey: ['solicitations', q, status, hasSkus, itOnly, sort],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (q) qs.set('q', q);
      if (status !== 'all') qs.set('status', status);
      if (hasSkus) qs.set('has_skus', 'true');
      if (itOnly) qs.set('it', 'true');
      if (sort) qs.set('sort', sort);
      return apiClient.get(`/api/solicitations?${qs.toString()}`);
    },
    // v5 spelling. The old `keepPreviousData: true` was silently inert here —
    // react-query 5 dropped the flag — so every keystroke in the search box
    // and every sort click unmounted the whole table and re-mounted it. That
    // also takes the header buttons with it, which is how the sort control
    // loses the click you are about to make.
    placeholderData: keepPreviousData,
  });

  const active = parseSort(sort);

  /* First click on a new column sorts descending for dates and counts (the
     interesting end of both) and ascending for text. Clicking the active
     column flips it. */
  const toggleSort = (key) => {
    if (active.key === key) { setSort(`${active.desc ? '' : '-'}${key}`); return; }
    const textual = key === 'title' || key === 'department' || key === 'id';
    setSort(`${textual ? '' : '-'}${key}`);
  };

  const SortIcon = ({ col }) => {
    if (active.key !== col) return <ChevronsUpDown size={12} aria-hidden="true" />;
    return active.desc
      ? <ArrowDown size={12} aria-hidden="true" />
      : <ArrowUp size={12} aria-hidden="true" />;
  };

  return (
    <div className="sol-detail-shell">
      {/* One header line + one filter line on the page surface — the
          boxed header-card-above-table-card was double chrome (audit §5). */}
      <PageHeader
        title="Solicitations"
        blurb="California state RFQs, IFBs, and RFPs ingested from Cal eProcure. Click a row for the virtual document view — typed sections, SKU table, and linked POs."
      />
      <div className="sol-list-filters">
          <input
            type="search"
            placeholder="Search title…"
            value={q}
            onChange={e => setQ(e.target.value)}
            className="sol-filter-input"
          />
          <select value={status} onChange={e => setStatus(e.target.value)} className="sol-filter-select">
            <option value="all">All statuses</option>
            <option value="Posted">Posted (active)</option>
            <option value="Event Completed">Closed / completed</option>
          </select>
          <label className="sol-filter-toggle">
            <input
              type="checkbox"
              checked={hasSkus}
              onChange={e => setHasSkus(e.target.checked)}
            /> SKUs only
          </label>
          {/* Most of this corpus is janitorial, HVAC and pavement. The title
              is the only signal Cal eProcure publishes, so the rule lives in
              services/procurement/solicitationItFilter and the tooltip says
              plainly that it reads titles — an unexplained filter that drops
              96% of the rows reads as a bug. */}
          <label
            className="sol-filter-toggle"
            title="Keeps technology purchases and drops facilities work, by reading the title. Physical security — guards, fences, alarm monitoring, cameras — counts as facilities."
          >
            <input
              type="checkbox"
              checked={itOnly}
              onChange={e => setItOnly(e.target.checked)}
            /> IT only
          </label>
        </div>

      <div className="sol-section-card">
        {isLoading && <div className="sol-loading">Loading…</div>}
        {error && <div className="sol-error">{error.message}</div>}
        {data && data.rows && (
          <>
            <div className="sol-section-subtitle" style={{ marginBottom: 12 }}>
              {/* `total` is what MATCHED; `count` is what this page holds. The
                  two differ whenever the limit bites, and showing the page
                  size as the result count made every filter look inert. */}
              {(data.total ?? data.count).toLocaleString()} solicitations
              {data.truncated && ` — showing the first ${data.count}`}
            </div>
            {/* Same scroll wrapper the detail page uses — 7 columns clip
                at phone width without it (slice 7b). */}
            <div className="sol-sku-table-wrap">
            <table className="sol-sku-table">
              <thead>
                <tr>
                  {COLUMNS.map(c => (
                    <th
                      key={c.key}
                      className={c.num ? 'sol-num' : undefined}
                      aria-sort={active.key === c.key ? (active.desc ? 'descending' : 'ascending') : 'none'}
                    >
                      <button
                        type="button"
                        className={`sol-sort-btn${active.key === c.key ? ' is-active' : ''}`}
                        onClick={() => toggleSort(c.key)}
                      >
                        {c.label}<SortIcon col={c.key} />
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map(r => (
                  <tr key={r.solicitation_id}>
                    <td className="sol-sku-code">
                      <Link to={`/solicitations/${encodeURIComponent(r.solicitation_id)}`}>{r.solicitation_id}</Link>
                    </td>
                    <td className="sol-sku-desc">
                      <Link to={`/solicitations/${encodeURIComponent(r.solicitation_id)}`}>{r.title || '(untitled)'}</Link>
                    </td>
                    <td>{r.department_name_raw || '—'}</td>
                    <td>{fmtDateTime(r.close_date)}</td>
                    <td className="sol-num">{r.attachment_count}</td>
                    <td className="sol-num">{r.section_count}</td>
                    <td className="sol-num">{r.sku_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {data.rows.length === 0 && (
              <div className="sol-loading">
                No solicitations match these filters.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
